import { RNG } from "rot-js";
import { QueryBuilder } from "word-graphs";
import { BreedTypes } from "../../data/breeds";
import { Actor } from "../actors/Actor";
import { getRampValue } from "../actors/Ramp";
import EntityManager from "../EntityManager";
import { gameData, changeLevel, getItemsForPosition } from "../GameManager";
import { WordList } from "../words/WordDictionary";
import { removeLetters, swapWord } from "../words/wordUtils";
import { StatAdjust } from "./StatAdjust";
import { Target, TargetOptions } from "./Target";
import { addLetters } from "../words/letters";
import { pickupItem } from "../items/itemUtils";
import { SoundSystem } from "../../sounds";

export class Effect {
  target: Target | undefined;
  submitWord: boolean | undefined;
  changeLevel: number | undefined;
  addXP: number | undefined;
  pickupAtTarget: boolean | undefined;

  statAdjustments: StatAdjust[] | undefined;

  letterCount: number | undefined;
  wandSwapLetters: boolean | undefined;
  wandLetterRemoval: boolean | undefined;
  placeLetters: boolean | undefined;

  changeWordToLength: number | undefined;

  prependLetters: string[] | undefined;
  appendLetters: string[] | undefined;

  grantFreeEntry: boolean;
  setNoTrigger: boolean;

  weaken: boolean;

  refreshSpells: number;

  constructor(data: any) {
    this.target = data.target && new Target(data.target);

    this.submitWord = data.submitWord;
    this.changeLevel = data.changeLevel;
    this.addXP = data.addXP;
    this.pickupAtTarget = data.pickupAtTarget;

    this.letterCount = data.letterCount;
    this.wandSwapLetters = data.wandSwapLetters;
    this.wandLetterRemoval = data.wandLetterRemoval;
    this.placeLetters = data.placeLetters;

    this.prependLetters = data.prependLetters;
    this.appendLetters = data.appendLetters;

    this.changeWordToLength = data.changeWordToLength;

    this.statAdjustments = data.statAdjustments?.map(
      (adjust: any) => new StatAdjust(adjust)
    );

    this.grantFreeEntry = data.grantFreeEntry;
    this.setNoTrigger = data.setNoTrigger;

    this.weaken = data.weaken;
    this.refreshSpells = data.refreshSpells;
  }

  apply(target: Actor, sourceTarget: TargetOptions) {
    const affected = sourceTarget.actor || target;

    if (this.addXP) {
      gameData.xp += this.addXP;
    }

    if (this.statAdjustments) {
      this.statAdjustments.forEach((adjustor) => {
        adjustor.apply(affected);
      });
    }

    if (this.wandLetterRemoval || this.wandSwapLetters) {
      affected.spellWand = removeLetters(
        affected.spellWand,
        sourceTarget.letters || []
      );

      if (this.wandSwapLetters) {
        addLetters(affected, sourceTarget.letters?.length || 1);
      }
    }

    this.applyChangeWord(affected, sourceTarget);

    if (this.placeLetters) {
      this.applyPlaceLetters(affected, sourceTarget);
    }

    if (this.grantFreeEntry) {
      gameData.freeEntry = true;
    }

    if (this.setNoTrigger) {
      gameData.noWordTriggers = true;
    }

    if (this.letterCount) {
      addLetters(affected, this.letterCount);
    }

    if (
      this.weaken &&
      affected.aiProps.words &&
      affected.aiProps.words.length > 1
    ) {
      affected.aiProps.words.pop();
    }

    if (this.changeLevel) {
      changeLevel();
    }

    if (this.refreshSpells) {
      affected.book.forEach((spell) => {
        spell.uses && spell.uses.change(this.refreshSpells);
      });
    }

    if (this.pickupAtTarget && sourceTarget.tile) {
      const items = getItemsForPosition(
        sourceTarget.tile.x,
        sourceTarget.tile.y
      );
      items.forEach((item) => {
        if (!item.breed.furniture) {
          pickupItem(affected, item);
          SoundSystem.play("pickup");
        } else if (item.isUsable()) {
          SoundSystem.play("powerup");
          item.useItem(affected);
        }
      });
    }
  }

  applyPlaceLetters(affected: Actor, sourceTarget: TargetOptions) {
    if (sourceTarget.letters?.length && sourceTarget.tile) {
      EntityManager.createPiece(
        BreedTypes.genericBlock,
        sourceTarget.tile.x,
        sourceTarget.tile.y,
        undefined,
        sourceTarget.letters[0]
      );
    }
  }

  applyChangeWord(affected: Actor, sourceTarget: TargetOptions) {
    if (this.changeWordToLength != null) {
      const length =
        this.changeWordToLength === -1
          ? affected.pieces.size
          : this.changeWordToLength;

      const seeds = [];
      const amount = getRampValue({ 2: 1, 5: 2, 7: 3 }, length) || 0;
      for (let i = 0; i < amount; i++) {
        seeds.push(String.fromCharCode(RNG.getUniformInt(97, 122)));
      }
      const query = new QueryBuilder(WordList)
        .containsAll(...seeds)
        .minLength(length)
        .maxLength(length);
      const results = query.build()();
      const newWord = RNG.getItem(results) as string | undefined;
      if (newWord) {
        swapWord(
          affected,
          affected.breed.isCapital ? newWord.toLocaleUpperCase() : newWord
        );
      } else {
        console.warn("Did not find new word for changeWord");
      }
    }
  }
}
